import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import ClosedSpaceChecklistFormState from "../../../types/forms/Permits/Conditions/ClosedSpaceChecklistFormState";
import { CLOSED_SPACE_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/ClosedSpaceChecklist";

type ClosedSpaceChecklistModalProps = {
    checklistVersion: number;
    resources: Resources;
    onChange: (fn: (prevValue: ClosedSpaceChecklistFormState) => ClosedSpaceChecklistFormState) => void;
    formState: ClosedSpaceChecklistFormState;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function ClosedSpaceChecklistModal({
    checklistVersion,
    resources,
    onChange,
    formState,
    show,
    onHide,
    canManageContent
}: ClosedSpaceChecklistModalProps) {
    function changeHandler(key: keyof ClosedSpaceChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: ClosedSpaceChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListClosedSpace")}</h1>
            </Modal.Header>
            <Modal.Body>
                {checklistVersion === CLOSED_SPACE_CHECKLIST_VERSION_2026_02 ? (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: ClosedSpaceChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof ClosedSpaceChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-all-terms-met"
                    labelText={resources.get("AreAllTermsMet")}
                    onChange={(updatedValue) => changeHandler("areAllTermsMet", updatedValue)}
                    resources={resources}
                    value={formState.areAllTermsMet.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-there-a-rescue-plan"
                    labelText={resources.get("IsThereARescuePlan")}
                    onChange={(updatedValue) => changeHandler("isThereARescuePlan", updatedValue)}
                    resources={resources}
                    value={formState.isThereARescuePlan.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-manhole-guard-available"
                    labelText={resources.get("IsManHoleGuardAvailable")}
                    onChange={(updatedValue) => changeHandler("isManHoleGuardAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isManHoleGuardAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-gas-measurements-performed"
                    labelText={resources.get("AreGasMeasurementsPerformed")}
                    onChange={(updatedValue) => changeHandler("areGasMeasurementsPerformed", updatedValue)}
                    resources={resources}
                    value={formState.areGasMeasurementsPerformed.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-warning-signs-on-manholes"
                    labelText={resources.get("AreWarningSignsOnManholes")}
                    onChange={(updatedValue) => changeHandler("areWarningSignsOnManholes", updatedValue)}
                    resources={resources}
                    value={formState.areWarningSignsOnManholes.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lightning-sufficient"
                    labelText={resources.get("IsLightningSufficient")}
                    onChange={(updatedValue) => changeHandler("isLightningSufficient", updatedValue)}
                    resources={resources}
                    value={formState.isLightningSufficient.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-there-emergency-equipment"
                    labelText={resources.get("IsThereEmergencyEquipment")}
                    onChange={(updatedValue) => changeHandler("isThereEmergencyEquipment", updatedValue)}
                    resources={resources}
                    value={formState.isThereEmergencyEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-gas-measurements-performed-in-closed-space"
                    labelText={resources.get("AreGasMeasurementsPerformedInClosedSpace")}
                    onChange={(updatedValue) => changeHandler("areGasMeasurementsPerformedInClosedSpace", updatedValue)}
                    resources={resources}
                    value={formState.areGasMeasurementsPerformedInClosedSpace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-personal-at-entry-to-closed-space"
                    labelText={resources.get("IsPersonalAtEntryToClosedSpace")}
                    onChange={(updatedValue) => changeHandler("isPersonalAtEntryToClosedSpace", updatedValue)}
                    resources={resources}
                    value={formState.isPersonalAtEntryToClosedSpace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-mechanical-ventilation"
                    labelText={resources.get("IsMechanicalVentilation")}
                    onChange={(updatedValue) => changeHandler("isMechanicalVentilation", updatedValue)}
                    resources={resources}
                    value={formState.isMechanicalVentilation.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="has-rescue-harness"
                    labelText={resources.get("HasRescueHarness")}
                    onChange={(updatedValue) => changeHandler("hasRescueHarness", updatedValue)}
                    resources={resources}
                    value={formState.hasRescueHarness.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="will-there-be-rescue-plan"
                    labelText={resources.get("WillThereBeRescuePlan")}
                    onChange={(updatedValue) => changeHandler("willThereBeRescuePlan", updatedValue)}
                    resources={resources}
                    value={formState.willThereBeRescuePlan.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="will-you-have-a-rescue-harness"
                    labelText={resources.get("WillYouHaveARescueHarness")}
                    onChange={(updatedValue) => changeHandler("willYouHaveARescueHarness", updatedValue)}
                    resources={resources}
                    value={formState.willYouHaveARescueHarness.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="will-there-be-emergency-equipment"
                    labelText={resources.get("WillThereBeEmergencyEquipment")}
                    onChange={(updatedValue) => changeHandler("willThereBeEmergencyEquipment", updatedValue)}
                    resources={resources}
                    value={formState.willThereBeEmergencyEquipment.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-trained-manhole-guard-available"
                    labelText={resources.get("IsTrainedManHoleGuardAvailable")}
                    onChange={(updatedValue) => changeHandler("isTrainedManHoleGuardAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isTrainedManHoleGuardAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-other-personnel-at-entry"
                    labelText={resources.get("IsOtherPersonnelAtEntry")}
                    onChange={(updatedValue) => changeHandler("isOtherPersonnelAtEntry", updatedValue)}
                    resources={resources}
                    value={formState.isOtherPersonnelAtEntry.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="will-gas-measurements-be-performed-and-documented"
                    labelText={resources.get("WillGasMeasurementsBePerformedAndDocumented")}
                    onChange={(updatedValue) => changeHandler("willGasMeasurementsBePerformedAndDocumented", updatedValue)}
                    resources={resources}
                    value={formState.willGasMeasurementsBePerformedAndDocumented.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-gas-measurements-performed-in-closed-space"
                    labelText={resources.get("AreGasMeasurementsPerformedInClosedSpace")}
                    onChange={(updatedValue) => changeHandler("areGasMeasurementsPerformedInClosedSpace", updatedValue)}
                    resources={resources}
                    value={formState.areGasMeasurementsPerformedInClosedSpace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="are-warning-signs-on-openings"
                    labelText={resources.get("AreWarningSignsOnOpenings")}
                    onChange={(updatedValue) => changeHandler("areWarningSignsOnOpenings", updatedValue)}
                    resources={resources}
                    value={formState.areWarningSignsOnOpenings.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-mechanical-ventilation"
                    labelText={resources.get("IsMechanicalVentilation")}
                    onChange={(updatedValue) => changeHandler("isMechanicalVentilation", updatedValue)}
                    resources={resources}
                    value={formState.isMechanicalVentilation.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lightning-sufficient"
                    labelText={resources.get("IsLightningSufficient")}
                    onChange={(updatedValue) => changeHandler("isLightningSufficient", updatedValue)}
                    resources={resources}
                    value={formState.isLightningSufficient.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

export default ClosedSpaceChecklistModal;

